import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import NewInstructionModal from '@/components/feature/NewInstructionModal';
import InstructionAttachmentThumbnails from '@/components/feature/InstructionAttachmentThumbnails';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { useManagementInstructionsList } from '@/hooks/useManagementInstructionsList';
import {
  archiveInstruction,
  deleteInstruction,
  canHardDeleteInstruction,
  emitInstructionsSync,
  type ManagementInstructionRecord,
} from '@/lib/instructionUtils';

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  sent:      { bg: '#F3F4F6', text: '#374151' },
  seen:      { bg: '#F3F4F6', text: '#374151' },
  in_progress: { bg: '#FEF0E7', text: '#E8621A' },
  replied:   { bg: '#FFFBEB', text: '#F59E0B' },
  completed: { bg: '#F0FDF4', text: '#059669' },
  closed:    { bg: '#FEF2F2', text: '#EF4444' },
};

const PRIORITY_STYLES: Record<string, { border: string; bar: string }> = {
  high:   { border: 'border-[#E8621A]/30', bar: 'bg-[#E8621A]' },
  medium: { border: 'border-gray-100',     bar: 'bg-[#059669]/30' },
  low:    { border: 'border-gray-100',     bar: 'bg-gray-200' },
};

function fmtDate(d: string | null): string {
  return formatDate(d);
}

function fmtTime(d: string): string {
  return formatDateTime(d);
}

interface Props {
  projectId: string;
}

export default function ManagementInstructionsTab({ projectId }: Props) {
  const { t } = useTranslation();
  const {
    instructions,
    subcontractors,
    attachments,
    loading,
    refreshing,
    reload,
    hasData,
  } = useManagementInstructionsList({ projectId });

  const STATUS_LABELS: Record<string, string> = {
    sent: t('statusLabel.sent'),
    seen: t('statusLabel.seen'),
    in_progress: t('statusLabel.in_progress'),
    replied: t('statusLabel.replied'),
    completed: t('statusLabel.completed'),
    closed: t('statusLabel.closed'),
  };

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<ManagementInstructionRecord | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [hiddenInstructionIds, setHiddenInstructionIds] = useState<Set<string>>(new Set());

  const visibleInstructions = instructions.filter((ins) => !hiddenInstructionIds.has(ins.id));

  const filtered = visibleInstructions.filter((ins) => {
    if (statusFilter === 'all') return true;
    return ins.status === statusFilter;
  });

  const stats = {
    total: visibleInstructions.length,
    sent: visibleInstructions.filter((i) => i.status === 'sent').length,
    open: visibleInstructions.filter((i) => i.status === 'open').length,
    in_progress: visibleInstructions.filter((i) => i.status === 'in_progress').length,
    completed: visibleInstructions.filter((i) => i.status === 'completed').length,
    closed: visibleInstructions.filter((i) => i.status === 'closed').length,
  };

  const getSubName = (id: string | null) => {
    if (!id) return t('statusLabel.unassigned');
    const p = subcontractors.find((s) => s.id === id);
    return p?.full_name || p?.company_name || t('statusLabel.unknown');
  };

  async function updateStatus(id: string, newStatus: string) {
    await supabase.from('jphph_instructions').update({ status: newStatus }).eq('id', id);
    emitInstructionsSync(projectId);
  }

  async function handleDeleteInstruction(ins: ManagementInstructionRecord) {
    setActionError(null);
    const result = await deleteInstruction(ins);
    if (result.error) {
      setActionError(result.error);
      return;
    }
    setHiddenInstructionIds((prev) => new Set(prev).add(ins.id));
    setConfirmDeleteId(null);
    reload();
  }

  async function handleArchiveInstruction(id: string, projectId: string) {
    setActionError(null);
    const result = await archiveInstruction(id, projectId);
    if (result.error) {
      setActionError(result.error);
      return;
    }
    setHiddenInstructionIds((prev) => new Set(prev).add(id));
    reload();
  }

  const showInitialLoading = loading && !hasData;

  return (
    <div className="space-y-4">
      {refreshing && hasData && (
        <div className="flex items-center gap-2 px-1 py-1">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-sm" />
          <span className="text-[#92400E] text-[11px] font-medium">{t('progress.refreshing', 'Refreshing...')}</span>
        </div>
      )}

      {actionError && (
        <div className="px-3 py-2 rounded-xl bg-[#FEF2F2] border border-[#FECACA] text-[#EF4444] text-xs">
          {actionError}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="w-full overflow-x-auto scrollbar-hide">
          <div className="flex flex-nowrap gap-1.5 min-w-max">
            {[
              { key: 'all', label: t('instructions.filterAll'), count: stats.total },
              { key: 'sent', label: t('statusLabel.sent'), count: stats.sent },
              { key: 'open', label: t('instructions.open'), count: stats.open },
              { key: 'in_progress', label: t('statusLabel.in_progress'), count: stats.in_progress },
              { key: 'completed', label: t('statusLabel.completed'), count: stats.completed },
              { key: 'closed', label: t('statusLabel.closed'), count: stats.closed },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors min-h-[36px] ${
                  statusFilter === s.key ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                }`}
              >
                {s.label}
                {s.count > 0 && (
                  <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    statusFilter === s.key ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {s.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            <span className="hidden sm:inline">{t('instructions.newInstructionBtn2')}</span>
            <span className="sm:hidden">{t('mobile.sendInstruction')}</span>
          </button>
        </div>
      </div>

      {showInitialLoading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
        </div>
      )}

      {!showInitialLoading && filtered.length === 0 && (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-file-list-3-line text-5xl"></i>
          </div>
          <p className="text-[#374151] font-semibold mb-1">
            {statusFilter === 'all' ? t('instructions.noInstructionsYetMsg') : t('instructions.noStatusInstructions', { status: STATUS_LABELS[statusFilter]?.toLowerCase() || statusFilter })}
          </p>
          <p className="text-[#9CA3AF] text-sm">{t('instructions.createInstructionMsg')}</p>
          {statusFilter === 'all' && (
            <div className="mt-4">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center justify-center gap-2 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                {t('instructions.createFirstInstructionBtn2')}
              </button>
            </div>
          )}
        </div>
      )}

      {!showInitialLoading && (
        <div className="space-y-3">
          {filtered.map((ins) => {
            const isExpanded = expandedId === ins.id;
            const statusStyle = STATUS_STYLES[ins.status] ?? STATUS_STYLES.sent;
            const priorityStyle = PRIORITY_STYLES[ins.priority] ?? PRIORITY_STYLES.medium;
            const insAttachments = attachments[ins.id] || [];

            return (
              <div key={ins.id} className={`bg-white rounded-2xl border overflow-hidden ${priorityStyle.border}`}>
                <div className={`h-1 ${priorityStyle.bar}`}></div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h4 className="text-sm font-semibold text-[#1A1A1A] leading-snug">{ins.instruction_title}</h4>
                      </div>
                    </div>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
                      style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                    >
                      {STATUS_LABELS[ins.status] ?? ins.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-[10px] font-bold text-[#0891B2] bg-[#EFF6FF] px-2 py-0.5 rounded-full">
                      {t('instructions.toLabel')} {getSubName(ins.to_user_id)}
                    </span>
                    {ins.due_at && (
                      <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full">
                        {t('instructions.dueLabel')} {fmtDate(ins.due_at)}
                      </span>
                    )}
                    {ins.priority === 'high' && (
                      <span className="text-[10px] font-bold text-[#EF4444] bg-[#FEF2F2] px-2 py-0.5 rounded-full">
                        {t('instructions.highPriorityBadge')}
                      </span>
                    )}
                  </div>

                  <p className={`text-[#6B7280] text-sm leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                    {ins.body}
                  </p>
                  {ins.body.length > 120 && (
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : ins.id)}
                      className="text-[#059669] text-xs font-semibold cursor-pointer mt-1.5 whitespace-nowrap"
                    >
                      {isExpanded ? t('instructions.showLessBtn') : t('instructions.readMoreBtn')}
                    </button>
                  )}

                  {insAttachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-50">
                      <InstructionAttachmentThumbnails files={insAttachments} maxVisible={3} />
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setEditingInstruction(ins)}
                      className="px-3 py-1.5 rounded-lg bg-[#EFF6FF] text-[#0891B2] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#0891B2]/10 transition-colors"
                    >
                      <i className="ri-edit-line mr-0.5"></i>Edit
                    </button>
                    {ins.status !== 'closed' && (
                      <button
                        onClick={() => void handleArchiveInstruction(ins.id, ins.project_id)}
                        className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#6B7280] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200 transition-colors"
                      >
                        <i className="ri-archive-line mr-0.5"></i>Archive
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmDeleteId(ins.id)}
                      className="px-3 py-1.5 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#EF4444]/10 transition-colors"
                    >
                      <i className="ri-delete-bin-line mr-0.5"></i>Delete
                    </button>
                    {ins.status !== 'completed' && ins.status !== 'closed' && (
                      <button
                        onClick={() => updateStatus(ins.id, 'completed')}
                        className="px-3 py-1.5 rounded-lg bg-[#F0FDF4] text-[#059669] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#059669]/10 transition-colors"
                      >
                        <i className="ri-checkbox-circle-line mr-0.5"></i>{t('instructions.markCompletedBtn')}
                      </button>
                    )}
                    <span className="text-[#9CA3AF] text-xs ml-auto">{fmtTime(ins.created_at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(showCreateModal || editingInstruction) && (
        <NewInstructionModal
          show={showCreateModal || !!editingInstruction}
          onClose={() => {
            setShowCreateModal(false);
            setEditingInstruction(null);
          }}
          projectId={projectId}
          editingInstruction={editingInstruction}
          source="project-instructions-tab"
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingInstruction(null);
            reload();
          }}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDeleteId(null)} />
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full">
            <h4 className="text-[#1A1A1A] font-bold text-sm mb-2">Delete Instruction?</h4>
            <p className="text-[#6B7280] text-xs mb-4">
              {(() => {
                const ins = instructions.find((i) => i.id === confirmDeleteId);
                if (ins && canHardDeleteInstruction(ins)) {
                  return 'This internal instruction will be permanently deleted.';
                }
                return 'Sent instructions are archived instead of permanently deleted.';
              })()}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 rounded-xl bg-gray-100 text-[#374151] text-xs font-semibold cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const ins = instructions.find((i) => i.id === confirmDeleteId);
                  if (ins) void handleDeleteInstruction(ins);
                }}
                className="px-4 py-2 rounded-xl bg-[#EF4444] text-white text-xs font-semibold cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
